// Hoja "¿Como termino la llamada?". Sube con resorte al colgar; se arrastra 1:1 con el dedo,
// resiste arriba (rubber-band) y al soltar proyecta el impulso para decidir si cierra.
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { useTipificaciones } from '../data/queries';
import { usePhone } from '../hooks';
import { duration } from '../lib/format';
import { Icon } from '../lib/icons';
import { formatPhone } from '../lib/phone-number';
import { project, rubberband, SpringValue, VelocityTracker } from '../lib/spring';
import { phone } from './store';

export function WrapSheet() {
  const s = usePhone();
  const t = s.cierre;
  const tips = useTipificaciones();
  const qc = useQueryClient();
  const sheet = useRef<HTMLElement>(null);
  const scrim = useRef<HTMLDivElement>(null);
  const alto = useRef(420);
  const y = useRef<SpringValue | null>(null);
  const [tip, setTip] = useState<string | null>(null);
  const [notas, setNotas] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = sheet.current;
    const sc = scrim.current;
    if (!el || !sc) return;
    y.current ??= new SpringValue(600, (v) => {
      el.style.transform = `translate(-50%, ${v}px)`;
      sc.style.opacity = String(Math.max(0, Math.min(1, 1 - v / alto.current)));
    }, 0.4);
    if (t) {
      setTip(t.tipificacionId);
      setNotas(t.notas);
      setVisible(true);
      requestAnimationFrame(() => {
        alto.current = el.offsetHeight + 24;
        if (!y.current!.animating) y.current!.set(alto.current);
        y.current!.go(0, { damping: 1, response: 0.4 });
        el.querySelector<HTMLButtonElement>('.tip')?.focus({ preventScroll: true });
      });
    }
  }, [t]);

  const cerrar = (guardado: boolean, velocity = 0) => {
    y.current?.go(alto.current, {
      velocity,
      damping: 1,
      response: 0.32,
      done: () => {
        setVisible(false);
        phone.cerrarCierre(guardado);
      },
    });
  };

  const guardar = async () => {
    if (!t || !tip) return;
    setGuardando(true);
    const ok = await phone.guardarTipificacion(t, tip, notas);
    setGuardando(false);
    if (ok) {
      void qc.invalidateQueries({ queryKey: ['llamadas'] });
      void qc.invalidateQueries({ queryKey: ['tareas'] });
      cerrar(true);
    }
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.button > 0 || (e.target as HTMLElement).closest('button,textarea,input,label,select')) return;
    const sp = y.current;
    const el = sheet.current;
    if (!sp || !el) return;
    const y0 = sp.value;
    sp.set(y0);
    const sy = e.clientY;
    const tr = new VelocityTracker();
    el.setPointerCapture(e.pointerId);
    const mv = (ev: PointerEvent) => {
      let v = y0 + (ev.clientY - sy);
      if (v < 0) v = rubberband(v, alto.current);
      sp.set(v);
      tr.add(0, ev.clientY);
    };
    const up = () => {
      el.removeEventListener('pointermove', mv);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', up);
      const v = tr.velocity().y;
      if (sp.value + project(v) > alto.current * 0.45) cerrar(false, v);
      else sp.go(0, { velocity: v, damping: 0.8, response: 0.35 });
    };
    el.addEventListener('pointermove', mv);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
  };

  useEffect(() => {
    if (!t) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !(e.target as HTMLElement).closest('.island,.cmdk,.dialog')) cerrar(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const elegida = tips.data?.find((x) => x.id === tip);
  const nombre = t?.contacto?.nombre || (t ? formatPhone(t.numero) : '');
  return (
    <>
      <div className="sheet-scrim" ref={scrim} onClick={() => cerrar(false)} style={{ pointerEvents: t ? 'auto' : 'none', visibility: visible ? 'visible' : 'hidden' }} />
      <section className="sheet" ref={sheet} role="dialog" aria-modal="true" aria-labelledby="shTitle" onPointerDown={onPointerDown} style={{ visibility: visible ? 'visible' : 'hidden' }} data-testid="hoja-cierre">
        <div className="grab" aria-hidden>
          <span />
        </div>
        {t ? (
          <>
            <h3 id="shTitle">¿Cómo terminó la llamada?</h3>
            <div className="meta">
              {nombre}
              {t.contacto?.empresa ? ` · ${t.contacto.empresa}` : ''} · {t.dir === 'in' ? 'Entrante' : 'Saliente'} · {duration(t.duracion)}
            </div>
            <div className="tipg" role="radiogroup" aria-label="Resultado de la llamada">
              {tips.isLoading ? <span className="note">Cargando tipificaciones…</span> : null}
              {tips.data?.length === 0 ? <span className="note">No hay tipificaciones creadas en el CRM.</span> : null}
              {(tips.data ?? []).map((x) => (
                <button key={x.id} className="tip" role="radio" aria-checked={tip === x.id} aria-pressed={tip === x.id} onClick={() => setTip(x.id)}>
                  {x.nombre}
                </button>
              ))}
            </div>
            {elegida?.requiereSeguimiento ? (
              <div className="follow">
                <Icon name="task" /> Se creará una tarea de seguimiento para mañana.
              </div>
            ) : null}
            <label htmlFor="shNote" className="flabel">
              Notas
            </label>
            <textarea id="shNote" value={notas} onChange={(e) => setNotas(e.target.value)} placeholder="Qué se habló, qué sigue…" style={{ marginTop: 6 }} />
            <div className="sh-f">
              <button className="btn ghost" onClick={() => cerrar(false)}>
                Ahora no
              </button>
              <button className="btn primary" onClick={guardar} disabled={!tip || guardando} data-testid="guardar-tipificacion">
                {guardando ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </>
        ) : null}
      </section>
    </>
  );
}

// Despues de una llamada interna (D69): tipificarla es opcional. Un boton discreto unos segundos.
export function TipificarInterna() {
  const s = usePhone();
  const t = s.internaReciente;
  if (!t || s.cierre) return null;
  return (
    <div className="interna-tip" role="status">
      <span>
        Llamada interna con {t.contacto?.nombre || `la extensión ${t.numero}`} · {duration(t.duracion)}
      </span>
      <button className="btn sm" type="button" onClick={() => phone.tipificarInterna()}>
        Tipificar
      </button>
      <button className="btn sm ghost" type="button" aria-label="No tipificar" onClick={() => phone.descartarInterna()}>
        <Icon name="x" size={14} />
      </button>
    </div>
  );
}
